import type { ResponseTally } from './api.ts'
import { CheckCell } from './findingsList.ts'
import { RECESSIVE, SERIES } from '../components/charts/palette.ts'
import type { FindingsHelpContent } from '../components/findings-report/FindingsHelp.tsx'

/** One vocabulary for the flow, its drill-down and the table. */
export const RESPONSE_COLOR = {
  total: SERIES.blue,
  responded: SERIES.blue,
  agreed: SERIES.aqua,
  disputed: SERIES.orange,
  confirmed: 'var(--color-fill-success-strong)',
  errors: 'var(--color-fill-error-strong)',
  pendingAudit: SERIES.yellow,
  pendingConclusion: RECESSIVE.strong,
  awaitingTriage: SERIES.yellow,
  unreviewedAgreed: RECESSIVE.strong,
  unanswered: RECESSIVE.weak,
} as const

const source =
  'Resultados atuais e histórico de respostas das conferências por dia de entrada na conferência no período.'
const unique =
  'Uma vez por conferência e identidade do apontamento, pela resposta válida mais recente.'
const divergenceOnly = 'Itens não avaliados, informativos e sem divergência.'

export const RESPONSE_HELP: Readonly<Partial<Record<CheckCell, FindingsHelpContent>>> = {
  [CheckCell.Total]: {
    calculation: 'Conta somente divergências: situações que a comparação apontou como erro.',
    source,
    includes: unique,
    excludes: divergenceOnly,
    provisional:
      'Disponibilidade não comprova visualização. Um item removido em nova comparação sem resposta histórica não pode ser recuperado.',
  },
  [CheckCell.Responded]: {
    calculation: 'Concordâncias + contestações com decisão salva, mesmo antes de Concluir.',
    source,
    includes: unique,
    excludes: 'Sem resposta e ' + divergenceOnly.toLocaleLowerCase('pt-BR'),
    provisional: 'Respostas provisórias podem ser revistas ou retiradas até Concluir.',
  },
  [CheckCell.Agreed]: {
    calculation: 'Decisões “marcar para correção” sobre uma divergência.',
    source: 'Resposta salva pelo conferente.',
    includes: 'Respostas provisórias e concluídas.',
    excludes: 'Não comprova correção no ERP nem confirma acerto da IA pela triagem.',
  },
  [CheckCell.Disputed]: {
    calculation: 'Decisões de que o apontamento não era um problema.',
    source: 'Resposta salva pelo conferente.',
    includes: 'Contestações triadas e ainda sem triagem.',
    excludes: 'Uma contestação sozinha não confirma erro da IA.',
    provisional:
      'Respostas elegíveis entram na fila de feedbacks quando salvas. A ausência do evento não informa o motivo nem se houve conclusão.',
  },
  [CheckCell.Confirmed]: {
    calculation: 'Contestações em que a triagem discordou do conferente e validou o apontamento.',
    source: 'Triagem humana no acompanhamento de feedbacks.',
    includes: 'Somente divergências com contestação e veredito humano.',
    excludes: 'Concordâncias operacionais, contestações pendentes e sem resposta.',
  },
  [CheckCell.Error]: {
    calculation:
      'Contestações em que a triagem concordou com o conferente: o apontamento era indevido.',
    source: 'Triagem humana no acompanhamento de feedbacks.',
    includes: 'Somente erros confirmados na triagem de uma divergência.',
    excludes: 'Concordâncias operacionais, contestações pendentes e sem resposta.',
  },
  [CheckCell.PendingAudit]: {
    calculation: 'Contestações que ainda não têm veredito da triagem humana.',
    source: 'Decisões salvas, registros de conclusão no histórico e triagem da contestação.',
    includes: 'Respostas elegíveis, com ou sem registro de conclusão, ainda sem classificação.',
    excludes: 'Concordâncias, que ficam separadas.',
    provisional:
      'A ausência de registro de conclusão não informa se a conferência permanece aberta.',
  },
  [CheckCell.PendingConclusion]: {
    calculation:
      'Contestações cuja resposta atual não tem um evento de feedback disponível para triagem.',
    source: 'Resposta atual e eventos de feedback associados ao apontamento.',
    includes:
      'Contestações em que a resposta atual não aparece como evento disponível para triagem, inclusive registros anteriores à entrada automática na fila ao salvar.',
    excludes:
      'A ausência do evento não informa sua causa. Esta resposta não conta como acerto nem erro e não é uma indicação de que ainda aguarda triagem.',
    provisional:
      'Respostas elegíveis são registradas na fila quando salvas; esta categoria não atribui a ausência do evento a uma falta de conclusão.',
  },
  [CheckCell.AwaitingTriage]: {
    calculation: 'Contestações elegíveis na fila de triagem que ainda não têm veredito humano.',
    source: 'Evento da resposta e triagem da contestação.',
    includes:
      'Respostas disponíveis para triagem e ainda sem classificação, com ou sem registro de conclusão.',
    excludes:
      'Respostas sem evento disponível ficam fora. Sem veredito não conta como acerto nem erro.',
  },
  [CheckCell.UnreviewedAgreed]: {
    calculation: 'Concordâncias operacionais sem validação do acerto do apontamento.',
    source: 'Decisão “marcar para correção”.',
    includes: 'Todas as concordâncias, sem presumir um resultado da IA.',
    excludes:
      'Não entram no acerto ou erro confirmado. A triagem de uma nota sobre a correção não valida se a divergência era correta.',
  },
  [CheckCell.Unanswered]: {
    calculation: 'Divergências sem decisão válida salva.',
    source,
    includes: 'Conferências abertas, encerradas ou na produção.',
    excludes: 'Não contam como acerto nem erro, mesmo em conferências finalizadas.',
    provisional: 'A ausência de resposta não permite inferir se houve correção no ERP.',
  },
}

/** The numerators are audited errors; the bases describe different populations. */
export function confirmedErrorRates(responses: ResponseTally) {
  const audited = responses.confirmed + responses.errors
  return {
    total: responses.total === 0 ? undefined : responses.errors / responses.total,
    responded: responses.responded === 0 ? undefined : responses.errors / responses.responded,
    audited: audited === 0 ? undefined : responses.errors / audited,
  }
}
